import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuditYear } from '../contexts/AuditYearContext.tsx';
import { useSoxControl } from '../hooks/controls/useControls.ts';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  FileText,
  FileSpreadsheet,
  Calendar,
  Play,
  Sparkles,
  Download,
  Trash2,
  RotateCcw,
  FileImage,
  File as FileIcon,
  Upload,
  X,
  Loader2,
  Info,
} from 'lucide-react';
import toast from 'react-hot-toast';
import axiosInstance from '../api/axios';
import { getApiErrorMessage, resolve403ErrorMessage } from '../request/getApiErrorMessage.ts';
import { MODULE_KEYS } from '../constants/moduleAccess.ts';
import { useRequireModuleAccess } from '../hooks/useRequireModuleAccess.ts';
import { useActivityLogFeature } from '../hooks/control-testing/useActivityLogFeature.ts';
import ModuleAccessDeniedDialog from './shared/ModuleAccessDeniedDialog.tsx';
import { format, parseISO } from 'date-fns';
import { validateFileSizes } from '../utils/controlConstants';
import { ControlOverviewCard } from '../features/controls/components/ControlFormFields.tsx';
import { ControlConfigureTestModal } from '../features/controls/components/ControlConfigureTestModal.tsx';
import {
  canRunControlTest,
  controlNeedsTestConfiguration,
  formatConfigureTestBlockedMessage,
  getMissingConfigureTestFields,
  isControlUuid,
  resolveControlUuid,
} from '../features/controls/functional.ts';
import { Tooltip } from '../admin/components/shared/Tooltip.tsx';
import { mapApiToDisplayStatus } from '../features/control-testing/utils/mapRow.ts';
import DocumentPreviewModal from './DocumentPreviewModal';
import FileDropzone from './FileDropzone';
import TestDesignTab from './test-design/TestDesignTab';
import TestRunsTab from './test-runs/TestRunsTab';
import EvidenceStoreTab from './test-runs/EvidenceStoreTab';

function ConfigureTestAction({
  fullWidth = false,
  pending,
  canConfigure,
  blockedMessage,
  onConfigure,
}) {
  return (
    <div className={fullWidth ? 'flex w-full items-center gap-2' : 'inline-flex items-center gap-2'}>
      <button
        type="button"
        onClick={() => { void onConfigure(); }}
        disabled={pending || !canConfigure}
        className={
          fullWidth
            ? 'flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2'
            : 'px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2'
        }
      >
        {pending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Sparkles className="h-4 w-4" />
        )}
        {pending ? 'Saving...' : 'Configure Test'}
      </button>
      {!canConfigure && blockedMessage ? (
        <Tooltip content={blockedMessage} side="top">
          <span
            className="inline-flex shrink-0 text-gray-500 hover:text-gray-700 cursor-help"
            tabIndex={0}
            role="img"
            aria-label={blockedMessage}
          >
            <Info className="h-4 w-4" />
          </span>
        </Tooltip>
      ) : null}
    </div>
  );
}

const formatPeriodRange = (start, end) => {
  if (!start || !end) return 'Evergreen';
  const startDate = parseISO(String(start).split('T')[0]);
  const endDate = parseISO(String(end).split('T')[0]);
  return `${format(startDate, 'MMM d')} — ${format(endDate, 'MMM d')}, ${format(endDate, 'yyyy')}`;
};

// Helper function to get appropriate icon and color based on file extension
const getFileIcon = (filename) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  if (['xlsx', 'xls', 'csv'].includes(ext)) {
    return { Icon: FileSpreadsheet, color: 'text-green-600' };
  }
  if (['pdf'].includes(ext)) {
    return { Icon: FileText, color: 'text-red-600' };
  }
  if (['doc', 'docx'].includes(ext)) {
    return { Icon: FileText, color: 'text-blue-600' };
  }
  if (['png', 'jpg', 'jpeg', 'gif'].includes(ext)) {
    return { Icon: FileImage, color: 'text-purple-600' };
  }
  return { Icon: FileIcon, color: 'text-gray-600' };
};

// Helper component for rendering a file icon by filename (replaces IIFEs)
const FileIconBadge = ({ filename, className = 'h-5 w-5' }) => {
  const { Icon, color } = getFileIcon(filename);
  return <Icon className={`${className} ${color}`} />;
};

// Helper component for last test display (replaces IIFE)
const LastTestDisplay = ({ testRunHistory }) => {
  const lastCompleted = testRunHistory.find(r => r.status !== 'Interrupted');
  if (!lastCompleted) return <div><label className="block font-medium mb-1 text-sm text-gray-600">Last Tested</label><p className="text-sm text-gray-500">Never</p></div>;
  return (
    <div>
      <label className="block font-medium mb-1 text-sm text-gray-600">Last Tested</label>
      <p className="text-sm text-gray-900">{lastCompleted.date}</p>
      <div className="flex items-center gap-2 mt-1">
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
          lastCompleted.status === 'Passed' ? 'bg-green-100 text-green-800' :
          lastCompleted.status === 'Failed' ? 'bg-red-100 text-red-800' :
          'bg-amber-100 text-amber-800'
        }`}>{lastCompleted.status}</span>
        <span className="text-xs text-gray-500">{lastCompleted.total_exceptions || 0} exceptions</span>
      </div>
    </div>
  );
};

function ViewControl() {
  const navigate = useNavigate();
  const location = useLocation();
  // Off by default; ?activitylog=1 / ?activitylog=0 switches the run activity log on / off for this tab's session.
  const { enabled: activityLogEnabled } = useActivityLogFeature();
  const { selectedAuditYear } = useAuditYear();
  const isYearLocked = Boolean(selectedAuditYear?.is_locked);
  const { id: routeControlId } = useParams();
  const {
    data: control,
    isLoading: loading,
    error: controlQueryError,
    refetch: refetchControl,
  } = useSoxControl(routeControlId);
  const controlUuid = resolveControlUuid(control, routeControlId);
  const {
    checkAccess: checkTestExecutionsModuleAccess,
    dialogProps: testExecutionsModuleAccessDialogProps,
  } = useRequireModuleAccess(MODULE_KEYS.TEST_EXECUTIONS, {
    hint:
      'Re-running a test from the control view requires the Test Executions module. Contact your organization administrator if you need access.',
  });
  const returnPath = location.state?.returnPath;
  const error = controlQueryError?.message ?? null;

  const handleBack = useCallback(() => {
    navigate(returnPath || '/sox-controls');
  }, [navigate, returnPath]);
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'overview');
  const [showConfigureTestModal, setShowConfigureTestModal] = useState(false);
  const [configureTestPending, setConfigureTestPending] = useState(false);
  const overviewCardRef = useRef(null);
  const [testRunHistory, setTestRunHistory] = useState([]);

  // Test Procedures state
  const [editableSteps, setEditableSteps] = useState([]);
  const [stepsModified, setStepsModified] = useState(false);
  // Read by the reload effect below without making it depend on the flag.
  const stepsModifiedRef = useRef(false);
  stepsModifiedRef.current = stepsModified;
  // Which control the unsaved steps belong to: opening another control drops them (they are not its steps).
  const stepsControlRef = useRef(null);
  const [stepsSaving, setStepsSaving] = useState(false);
  const [editableFrequency, setEditableFrequency] = useState('');

  // Initialize editable steps when control loads. A reload while the steps hold unsaved changes (for example the
  // one that follows saving the AI's population suggestion right after Generate) must not replace them with the
  // saved steps — for a new control those are empty, and the generated attributes and steps would vanish.
  useEffect(() => {
    if (controlUuid && stepsControlRef.current && stepsControlRef.current !== controlUuid) {
      stepsModifiedRef.current = false;
      setStepsModified(false);
    }
    if (controlUuid) stepsControlRef.current = controlUuid;
    if (control?.test_steps && !stepsModifiedRef.current) {
      setEditableSteps(control.test_steps.map(s => ({
        ...s,
        _text: s.summary || s.description || s.step_description || ''
      })));
      setStepsModified(false);
    }
    if (control?.frequency) {
      setEditableFrequency(control.frequency);
    }
  }, [control, controlUuid]);

  const handleSaveSteps = async () => {
    setStepsSaving(true);
    try {
      const stepsPayload = editableSteps.map((s, i) => {
        const { _text, ...rest } = s;
        // Invariant I16: drop attributes with empty description at save time
        const cleanedAttrs = (rest.test_attributes || []).filter(
          a => (a.description || '').trim().length > 0
        );
        return {
          ...rest,
          summary: _text,
          description: _text,
          step_number: i + 1,
          ...(cleanedAttrs.length > 0 ? { test_attributes: cleanedAttrs } : {}),
        };
      });
      await axiosInstance.put(`/api/sox-controls/${controlUuid}`, {
        uuid: controlUuid,
        test_steps: stepsPayload,
        frequency: editableFrequency,
      });
      stepsModifiedRef.current = false;
      setStepsModified(false);
      await refetchControl();
    } catch (err) {
      console.error('Failed to save steps:', err);
    }
    setStepsSaving(false);
  };

  // Evidence Store state (Evidence Store)
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [evidencePeriods, setEvidencePeriods] = useState([]);
  const [selectedEvidencePeriod, setSelectedEvidencePeriod] = useState('all');
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadPeriodStart, setUploadPeriodStart] = useState('');
  const [uploadPeriodEnd, setUploadPeriodEnd] = useState('');
  const [showDeleteStoreEvidenceModal, setShowDeleteStoreEvidenceModal] = useState(false);
  const [storeEvidenceToDelete, setStoreEvidenceToDelete] = useState(null);
  const [isUploadingStoreEvidence, setIsUploadingStoreEvidence] = useState(false);
  // SCRUM-402 Phase 3: auditor picks evidence_type per upload batch so files
  // land in the right bucket when analyze-stream routes them (pbc -> evidence,
  // population_report -> role=population, verification -> role=verification).
  const [uploadEvidenceType, setUploadEvidenceType] = useState('pbc');
  const [previewFile, setPreviewFile] = useState(null);
  const [evidenceSearchQuery, setEvidenceSearchQuery] = useState('');

  const closeUploadModal = useCallback(() => {
    setShowUploadForm(false);
    setUploadPeriodStart('');
    setUploadPeriodEnd('');
    setUploadEvidenceType('pbc');
  }, []);

  // Modal state for actions
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState(null);

  // SCRUM-316: re-run with override. Prompts for an optional reason, then POSTs
  // to the backend which supersedes the prior run and creates a new pending row.
  // Navigates to the wizard so the user can kick off analysis.
  const [rerunModal, setRerunModal] = useState({ open: false, executionId: null });
  const [rerunReason, setRerunReason] = useState('');
  const [isRerunSubmitting, setIsRerunSubmitting] = useState(false);

  const confirmRerun = async () => {
    if (!rerunModal.executionId) return;
    if (!checkTestExecutionsModuleAccess()) return;
    setIsRerunSubmitting(true);
    try {
      const userHash = localStorage.getItem('userHash');
      const response = await axiosInstance.post(
        `/api/test-executions/${rerunModal.executionId}/rerun`,
        { rerun_reason: rerunReason.trim() || undefined },
        { headers: { 'Authorization': userHash || '' } }
      );
      toast.success(
        `Re-run created (RUN-${response.data?.test_run_number}). Prior run retained in history.`
      );
      setRerunModal({ open: false, executionId: null });
      navigate(`/sox-controls/${controlUuid}/run-test`, {
        state: {
          runTestReturnPath: `/sox-controls/view/${controlUuid}`,
          rerunFromExecutionId: rerunModal.executionId,
          newExecutionId: response.data?.new_execution_id,
        },
      });
    } catch (err) {
      console.error('Re-run failed:', err);
      toast.error(resolve403ErrorMessage(err, getApiErrorMessage(err, 'Failed to re-run')));
    } finally {
      setIsRerunSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!selectedRunId) return;
    try {
      const userHash = localStorage.getItem('userHash');
      await axiosInstance.delete(`/api/test-executions/${selectedRunId}`, {
        headers: { 'Authorization': userHash || '' }
      });
      // Remove from local state
      setTestRunHistory(prev => prev.filter(run => run.executionId !== selectedRunId));
      setShowDeleteModal(false);
      setSelectedRunId(null);
    } catch (err) {
      console.error('Error deleting execution:', err);
      alert(resolve403ErrorMessage(err, 'Failed to delete test run'));
    }
  };


  const [isDownloading, setIsDownloading] = useState(false);

  // Download handlers for Excel and PDF
  const handleDownloadExcel = async () => {
    if (!selectedRunId || isDownloading) return;
    setIsDownloading(true);

    try {
      const userHash = localStorage.getItem('userHash');

      // Fetch the test execution from backend (contains step_results, walkthrough, etc.)
      const response = await axiosInstance.get(`/api/test-executions/${selectedRunId}`, {
        headers: { 'Authorization': userHash || '' }
      });

      const execution = response.data;

      // Transform the execution data to match the expected test_result format
      const testResult = {
        control_id: execution.control_id || control?.control_id,
        walkthrough: execution.walkthrough_summary,
        step_results: execution.step_results || [],
        steps_passed: execution.steps_passed || 0,
        steps_failed: execution.steps_failed || 0,
        steps_warning: execution.steps_warning || 0,
        total_exceptions: execution.total_exceptions || 0,
        execution_started: execution.execution_started_at,
        execution_completed: execution.execution_completed_at,
        execution_duration_seconds: execution.execution_duration_seconds,
        files_analyzed: execution.files_analyzed || []
      };

      // Prepare request for AI service
      const requestBody = {
        test_result: testResult,
        control_title: control?.control_title || control?.title || '',
        test_steps: control?.test_steps || []
      };

      // Call Go backend proxy for Excel generation
      const excelResponse = await axiosInstance.post(
        '/api/test-executions/export-comprehensive-excel',
        requestBody,
        {
          responseType: 'blob',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      // Download the file
      const blob = new Blob([excelResponse.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${control?.control_id || controlUuid}_TestResults.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setShowDownloadModal(false);
    } catch (error) {
      console.error('Error downloading Excel:', error);
      alert(resolve403ErrorMessage(error, 'Failed to download Excel report. Please try again.'));
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!selectedRunId || isDownloading) return;
    setIsDownloading(true);

    try {
      const userHash = localStorage.getItem('userHash');

      // Fetch the test execution from backend (contains step_results, walkthrough, etc.)
      const response = await axiosInstance.get(`/api/test-executions/${selectedRunId}`, {
        headers: { 'Authorization': userHash || '' }
      });

      const execution = response.data;

      // Transform the execution data to match the expected test_result format
      const testResult = {
        control_id: execution.control_id || control?.control_id,
        walkthrough: execution.walkthrough_summary,
        step_results: execution.step_results || [],
        steps_passed: execution.steps_passed || 0,
        steps_failed: execution.steps_failed || 0,
        steps_warning: execution.steps_warning || 0,
        total_exceptions: execution.total_exceptions || 0,
        execution_started: execution.execution_started_at,
        execution_completed: execution.execution_completed_at,
        execution_duration_seconds: execution.execution_duration_seconds,
        files_analyzed: execution.files_analyzed || []
      };

      // Prepare request for AI service
      const requestBody = {
        test_result: testResult,
        control_title: control?.control_title || control?.title || '',
        test_steps: control?.test_steps || []
      };

      // Call Go backend proxy for PDF generation
      const pdfResponse = await axiosInstance.post(
        '/api/test-executions/export-comprehensive-pdf',
        requestBody,
        {
          responseType: 'blob',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      // Download the file
      const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${control?.control_id || controlUuid}_TestResults.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setShowDownloadModal(false);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert(resolve403ErrorMessage(error, 'Failed to download PDF report. Please try again.'));
    } finally {
      setIsDownloading(false);
    }
  };

  // Fetch test execution history (requires canonical UUID, not display control_id)
  useEffect(() => {
    const fetchTestHistory = async () => {
      if (!control) return;
      const executionControlUuid = resolveControlUuid(control, routeControlId);
      if (!executionControlUuid || !isControlUuid(executionControlUuid)) return;
      try {
        const userHash = localStorage.getItem('userHash');
        const response = await axiosInstance.get(
          `/api/test-executions/control/${executionControlUuid}?include_history=1`,
          {
          headers: { 'Authorization': userHash || '' }
        });
        const executions = response.data || [];
        // Transform to format expected by the UI
        const history = executions.map((exec) => {
          const started = exec.started_at ?? exec.created_at ?? null;
          return {
            id: `RUN-${exec.test_run_number}`,
            date: started ? new Date(started).toLocaleString() : 'N/A',
            status: mapApiToDisplayStatus(exec),
            overall_status: exec.overall_status || null,
            passed: exec.steps_passed || 0,
            steps_passed: exec.steps_passed || 0,
            failed: exec.steps_failed || 0,
            warnings: exec.steps_warning || 0,
            total_exceptions: exec.total_exceptions || 0,
            testing_period_start: exec.testing_period_start || null,
            testing_period_end: exec.testing_period_end || null,
            total_population: exec.total_population || null,
            total_sample_size: exec.total_sample_size || null,
            executionId: exec.id,
            isCurrent: exec.is_current !== false, // default TRUE for back-compat
            supersedesExecutionId: exec.supersedes_execution_id ?? null,
            // New Test Runs tab fields — pass raw backend values through
            test_run_number: exec.test_run_number,
            testRunNumber:   exec.test_run_number,
            test_status:     exec.test_status,
            testStatus:      exec.test_status,
            selected_periods: exec.selected_periods || null,
            population_data:  exec.population_data || null,
            files_analyzed:   exec.files_analyzed || [],
            created_at:       exec.created_at || started,
          };
        });
        setTestRunHistory(history);
      } catch (err) {
        console.log('No test history found:', err.message);
      }
    };
    fetchTestHistory();
  }, [control, routeControlId]);

  // Evidence Store fetch functions
  const fetchEvidence = useCallback(async () => {
    setEvidenceLoading(true);
    try {
      const userHash = localStorage.getItem('userHash');
      const headers = { 'Authorization': userHash || '' };
      const [filesRes, periodsRes] = await Promise.all([
        axiosInstance.get(`/api/sox-controls/${controlUuid}/evidence`, { headers }),
        axiosInstance.get(`/api/sox-controls/${controlUuid}/evidence/periods`, { headers })
      ]);
      setEvidenceFiles(filesRes.data || []);
      setEvidencePeriods(periodsRes.data || []);
    } catch (err) {
      console.error('Failed to fetch evidence:', err);
    }
    setEvidenceLoading(false);
  }, [controlUuid]);

  const fetchEvidenceForPeriod = useCallback(async (periodStart, periodEnd, filterType) => {
    setEvidenceLoading(true);
    try {
      const userHash = localStorage.getItem('userHash');
      const params = {};
      if (filterType === 'evergreen') {
        params.filter = 'evergreen';
      } else if (periodStart && periodEnd) {
        params.period_start = String(periodStart).split('T')[0];
        params.period_end = String(periodEnd).split('T')[0];
      }
      const res = await axiosInstance.get(`/api/sox-controls/${controlUuid}/evidence`, {
        headers: { 'Authorization': userHash || '' },
        params
      });
      setEvidenceFiles(res.data || []);
    } catch (err) {
      console.error('Failed to fetch evidence for period:', err);
    }
    setEvidenceLoading(false);
  }, [controlUuid]);

  // Evidence Store tab loads its own data internally via EvidenceStoreTab

  const showConfigureTestButton = Boolean(control) && controlNeedsTestConfiguration(control);
  // Configure Test / Configure with AI / Run Test and the Testing card are hidden unless the URL has ?oldsystem=1.
  const showOldSystemActions = new URLSearchParams(location.search).get('oldsystem') === '1';
  const showRunTestButton = canRunControlTest(control);
  const missingConfigureTestFields = useMemo(
    () => getMissingConfigureTestFields(control),
    [control]
  );
  const canConfigureTest = missingConfigureTestFields.length === 0 && !isYearLocked;
  const configureTestBlockedMessage = useMemo(
    () => isYearLocked
      ? 'This audit year is locked — no changes permitted'
      : formatConfigureTestBlockedMessage(missingConfigureTestFields),
    [missingConfigureTestFields, isYearLocked]
  );

  const allTabs = useMemo(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    () => [
      { id: 'overview',       label: 'Overview'       },
      { id: 'test-design',    label: 'Test Plan'      },
      { id: 'test-runs',      label: 'Test Runs'      },
      { id: 'evidence-store', label: 'Evidence Store' },
    ],
    []
  );

  // Always show all 4 tabs — Test Design handles the "no steps yet" state internally
  const tabs = allTabs;

  // All 4 tabs are always accessible regardless of control status

  const handleRunTest = useCallback(() => {
    if (!control.control_id) return;
    navigate(`/sox-controls/${control.control_id}/run-test`, {
      state: { runTestReturnPath: `/sox-controls/view/${control.control_id}` },
    });
  }, [control?.control_id, navigate]);

  const handleConfigureWithAI = useCallback(() => {
    if (!control?.control_id) return;
    navigate(`/sox-controls/${control.control_id}/ai-engagement`);
  }, [control?.control_id, navigate]);

  const handleConfigureTest = useCallback(async () => {
    if (!canConfigureTest) return;
    if (!overviewCardRef.current) return;
    setConfigureTestPending(true);
    try {
      const { ready, didPersist } = await overviewCardRef.current.prepareForConfigureTest();
      if (!ready) {
        return;
      }
      if (didPersist) {
        await refetchControl();
      }
      setShowConfigureTestModal(true);
    } finally {
      setConfigureTestPending(false);
    }
  }, [canConfigureTest, refetchControl]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-500">Loading control details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-3xl mx-auto bg-white rounded-lg shadow p-8 text-center">
          <div className="text-red-500 text-xl mb-4">Error Loading Control</div>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/sox-controls')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Back to Controls
          </button>
        </div>
      </div>
    );
  }

  if (!control) return null;

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      {/* Top Bar */}
      <div className="bg-white border-b border-gray-200 px-8 py-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4 min-w-0">
            <button
              onClick={handleBack}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
            >
              <ArrowLeft className="h-5 w-5 text-gray-500" />
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-bold text-gray-900">Control {control.control_id}</h1>
              </div>
              <p className="text-sm text-gray-500 mt-0.5 truncate">{control.title}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {showOldSystemActions && showConfigureTestButton && (
              <ConfigureTestAction
                pending={configureTestPending}
                canConfigure={canConfigureTest}
                blockedMessage={configureTestBlockedMessage}
                onConfigure={handleConfigureTest}
              />
            )}
            {showOldSystemActions && (
            <button
              type="button"
              onClick={isYearLocked ? undefined : handleConfigureWithAI}
              disabled={isYearLocked}
              title={isYearLocked ? 'This audit year is locked — no changes permitted' : undefined}
              className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Sparkles className="h-4 w-4" />
              Configure with AI
            </button>
            )}
            {showOldSystemActions && showRunTestButton && (
              <button
                type="button"
                onClick={handleRunTest}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 inline-flex items-center gap-2"
              >
                <Play className="h-4 w-4" />
                Run Test
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8 py-8">
        {/* Tab navigation — draft without test steps shows Overview only; other statuses show all tabs */}
        <div className="border-b border-gray-200 mb-8">
          <div className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative h-12 pb-3 pt-2 text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'text-gray-900 border-b-2 border-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              <div className="lg:col-span-3 space-y-6">
                <ControlOverviewCard
                  ref={overviewCardRef}
                  control={control}
                  controlUuid={controlUuid}
                  onSaved={() => {
                    void refetchControl();
                    if (returnPath) navigate(returnPath);
                  }}
                />
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Testing History — hidden unless ?oldsystem=1 */}
                {showOldSystemActions && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-gray-600" />
                    <h3 className="font-semibold text-gray-900">Testing</h3>
                  </div>
                  <div className="p-6 space-y-4">
                    <LastTestDisplay testRunHistory={testRunHistory} />
                    {showConfigureTestButton && (
                      <ConfigureTestAction
                        fullWidth
                        pending={configureTestPending}
                        canConfigure={canConfigureTest}
                        blockedMessage={configureTestBlockedMessage}
                        onConfigure={handleConfigureTest}
                      />
                    )}
                    <button
                      type="button"
                      onClick={isYearLocked ? undefined : handleConfigureWithAI}
                      disabled={isYearLocked}
                      title={isYearLocked ? 'This audit year is locked — no changes permitted' : undefined}
                      className="w-full px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Sparkles className="h-4 w-4" />
                      Configure with AI
                    </button>
                    {showRunTestButton && (
                      <button
                        type="button"
                        onClick={handleRunTest}
                        className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 inline-flex items-center justify-center gap-2"
                      >
                        <Play className="h-4 w-4" />
                        Run Test
                      </button>
                    )}
                  </div>
                </div>
                )}

                {/* Control Document */}
                {(control.control_document || control.control_upload_id) && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                      <h3 className="font-semibold text-gray-900">Control Document</h3>
                    </div>
                    <div className="p-6">
                      {control.control_document ? (
                        <button
                          onClick={() => setPreviewFile({ id: control.control_upload_id, name: control.control_document.name })}
                          className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 w-full text-left transition-colors"
                        >
                          <FileIconBadge filename={control.control_document.name} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{control.control_document.name}</p>
                            <p className="text-xs text-gray-500">{control.control_document.size}</p>
                          </div>
                        </button>
                      ) : (
                        <p className="text-sm text-gray-500 italic">No control document</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}


        {/* Test Design Tab */}
        {activeTab === 'test-design' && (
          <TestDesignTab
            control={control}
            controlUuid={controlUuid}
            testSteps={editableSteps}
            onStepChange={(stepIdx, newStep) => {
              setEditableSteps(prev => prev.map((s, i) => i === stepIdx ? newStep : s));
              // Editing or deleting a step inside an attribute is a change too: without this the
              // "Accept & Save" button never appears and the edit is silently lost on reload.
              setStepsModified(true);
            }}
            onReplaceSteps={(steps) => { setEditableSteps(steps); setStepsModified(true); }}
            handleSaveSteps={handleSaveSteps}
            stepsModified={stepsModified}
            stepsSaving={stepsSaving}
            onControlUpdated={() => void refetchControl()}
          />
        )}

        {/* Test Runs Tab — self-sufficient (fetches its own history) */}
        {activeTab === 'test-runs' && (
          <TestRunsTab
            control={control}
            controlUuid={controlUuid}
            activityLogEnabled={activityLogEnabled}
          />
        )}

        {/* Evidence Store Tab — common evidence + bifurcation + where-used */}
        {activeTab === 'evidence-store' && (
          <EvidenceStoreTab controlUuid={controlUuid} />
        )}
      </div>

      {/* Upload Evidence Modal — at root level like other modals */}
      {showUploadForm && (() => {
        const existingDated = evidencePeriods.filter(p => p.testing_period_end);
        let smartStart = new Date().toISOString().split('T')[0];
        if (existingDated.length > 0 && !uploadPeriodStart) {
          const lastEnd = existingDated.reduce((latest, p) => {
            const d = String(p.testing_period_end).split('T')[0];
            return d > latest ? d : latest;
          }, '');
          if (lastEnd) {
            const next = new Date(lastEnd + 'T00:00:00Z');
            next.setUTCDate(next.getUTCDate() + 1);
            smartStart = next.toISOString().split('T')[0];
          }
        }
        // Compute the correct select value from state
        const selectValue = uploadPeriodStart === '__evergreen__' ? '__evergreen__'
          : (uploadPeriodStart && uploadPeriodEnd && evidencePeriods.some(p => {
              const periodStart = String(p.testing_period_start).split('T')[0];
              const periodEnd = String(p.testing_period_end).split('T')[0];
              return periodStart === uploadPeriodStart && periodEnd === uploadPeriodEnd;
            })) ? `${uploadPeriodStart}_${uploadPeriodEnd}`
          : uploadPeriodStart ? '__new__'
          : '';
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            onClick={closeUploadModal}>
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-900">Upload Evidence</h3>
                <button onClick={closeUploadModal}
                  className="p-1 hover:bg-gray-100 rounded">
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                {/* SCRUM-402 Phase 3: evidence_type picker. Determines the
                    downstream role in analyze-stream — pbc -> evidence,
                    population_report -> role=population, verification ->
                    role=verification (C&A agents consume these). */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Upload as</label>
                  <select
                    value={uploadEvidenceType}
                    onChange={(e) => setUploadEvidenceType(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  >
                    <option value="pbc">Evidence (PBC) — mapped to test steps</option>
                    <option value="population_report">Population report — counted for sampling</option>
                    <option value="verification">Verification (GL, raw export, source docs) — C&amp;A checks</option>
                  </select>
                  <p className="text-[11px] text-gray-500 mt-1">
                    {uploadEvidenceType === 'population_report'
                      ? 'File is counted deterministically; drives the sample-size recommendation.'
                      : uploadEvidenceType === 'verification'
                        ? 'File is consumed by the Completeness & Accuracy agents. Not mapped to a step.'
                        : 'File is mapped to one or more test steps by the AI.'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Testing Period</label>
                  <select
                    value={selectValue}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '__new__') { setUploadPeriodStart(smartStart); setUploadPeriodEnd(''); }
                      else if (val === '__evergreen__') { setUploadPeriodStart('__evergreen__'); setUploadPeriodEnd('__evergreen__'); }
                      else if (val) { const [periodStart, periodEnd] = val.split('_'); setUploadPeriodStart(periodStart); setUploadPeriodEnd(periodEnd); }
                      else { setUploadPeriodStart(''); setUploadPeriodEnd(''); }
                    }}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  >
                    <option value="">Choose a period...</option>
                    {evidencePeriods.filter(p => p.testing_period_start && p.testing_period_end).map((p, i) => {
                      const periodStart = String(p.testing_period_start).split('T')[0];
                      const periodEnd = String(p.testing_period_end).split('T')[0];
                      return <option key={i} value={`${periodStart}_${periodEnd}`}>{formatPeriodRange(p.testing_period_start, p.testing_period_end)} ({p.file_count} files)</option>;
                    })}
                    <option value="__evergreen__">Evergreen (all periods)</option>
                    <option value="__new__">+ Create new period</option>
                  </select>
                </div>
                {uploadPeriodStart !== '' && uploadPeriodStart !== '__evergreen__' && (
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Start</label>
                      <input type="date" value={uploadPeriodStart} onChange={(e) => setUploadPeriodStart(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">End</label>
                      <input type="date" value={uploadPeriodEnd} onChange={(e) => setUploadPeriodEnd(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                    </div>
                  </div>
                )}
                {uploadPeriodStart && uploadPeriodEnd && uploadPeriodStart !== '__evergreen__' && uploadPeriodEnd < uploadPeriodStart && (
                  <p className="text-sm text-red-600">End date must be on or after the start date.</p>
                )}
                {((uploadPeriodStart && uploadPeriodEnd && (uploadPeriodStart === '__evergreen__' || uploadPeriodEnd >= uploadPeriodStart))) && (
                  <FileDropzone
                    multiple={true}
                    isUploading={isUploadingStoreEvidence}
                    label="Drop evidence files here or click to browse"
                    compact={true}
                    onFilesSelected={async (files) => {
                      const sizeErrors = validateFileSizes(files);
                      if (sizeErrors.length > 0) {
                        alert(sizeErrors.join('\n'));
                        return;
                      }
                      setIsUploadingStoreEvidence(true);
                      const userHash = localStorage.getItem('userHash');
                      for (const file of files) {
                        const formData = new FormData();
                        formData.append('file', file);
                        if (uploadPeriodStart !== '__evergreen__') {
                          formData.append('testing_period_start', uploadPeriodStart);
                          formData.append('testing_period_end', uploadPeriodEnd);
                        }
                        formData.append('evidence_type', uploadEvidenceType);
                        try {
                          await axiosInstance.post(`/api/sox-controls/${controlUuid}/evidence/upload`, formData, {
                            headers: { 'Authorization': userHash || '', 'Content-Type': 'multipart/form-data' }
                          });
                        } catch (err) {
                          console.error('Upload failed:', err);
                        }
                      }
                      setIsUploadingStoreEvidence(false);
                      setShowUploadForm(false);
                      setUploadPeriodStart('');
                      setUploadPeriodEnd('');
                      fetchEvidence();
                    }}
                  />
                )}
              </div>
              <div className="px-6 py-3 border-t border-gray-200 flex justify-end">
                <button onClick={closeUploadModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Re-run Confirmation Modal (SCRUM-316) */}
      {rerunModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-100 rounded-full">
                <RotateCcw className="h-5 w-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Re-run Test</h3>
            </div>
            <p className="text-gray-600 mb-4">
              This will become the source of truth. The prior run will remain in history (visible with <code className="text-xs bg-gray-100 px-1 rounded">?include_history=1</code>).
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-2">Reason (optional)</label>
            <textarea
              value={rerunReason}
              onChange={(e) => setRerunReason(e.target.value)}
              placeholder="e.g., New evidence received; sample size change"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none mb-6"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setRerunModal({ open: false, executionId: null })}
                disabled={isRerunSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmRerun}
                disabled={isRerunSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-60 inline-flex items-center gap-2"
              >
                {isRerunSubmitting && <RotateCcw className="h-3.5 w-3.5 animate-spin" />}
                Re-run
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Test Run</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this test run? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Download Modal */}
      {showDownloadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-green-100 rounded-full">
                <Download className="h-5 w-5 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Download Report</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Choose the format for your test execution report:
            </p>
            <div className="space-y-2 mb-6">
              <button
                onClick={handleDownloadExcel}
                disabled={isDownloading}
                className={`w-full px-4 py-3 text-left text-sm font-medium border border-gray-200 rounded-lg flex items-center gap-3 ${isDownloading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
              >
                <FileSpreadsheet className="h-5 w-5 text-green-600" />
                <div className="flex-1">
                  <p className="font-medium">Excel Workbook (.xlsx)</p>
                  <p className="text-xs text-gray-500">Full audit walkthrough with formatting</p>
                </div>
                {isDownloading && <span className="text-xs text-gray-400">Generating...</span>}
              </button>
              <button
                onClick={handleDownloadPdf}
                disabled={isDownloading}
                className={`w-full px-4 py-3 text-left text-sm font-medium border border-gray-200 rounded-lg flex items-center gap-3 ${isDownloading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
              >
                <FileText className="h-5 w-5 text-red-600" />
                <div className="flex-1">
                  <p className="font-medium">PDF Report (.pdf)</p>
                  <p className="text-xs text-gray-500">Printable summary report</p>
                </div>
                {isDownloading && <span className="text-xs text-gray-400">Generating...</span>}
              </button>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setShowDownloadModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <ControlConfigureTestModal
        open={showConfigureTestModal}
        controlUuid={controlUuid}
        control={control}
        onClose={() => setShowConfigureTestModal(false)}
        onSuccess={() => {
          void refetchControl();
          toast.success('Test steps saved. Control is now active.');
        }}
      />

      {/* Document Preview Modal */}
      <DocumentPreviewModal
        isOpen={!!previewFile}
        onClose={() => setPreviewFile(null)}
        fileId={previewFile?.id}
        fileName={previewFile?.name}
      />
      <ModuleAccessDeniedDialog {...testExecutionsModuleAccessDialogProps} />
    </div>
  );
}

export default ViewControl;
